import { useMemo, useCallback } from 'react';

import Chip from '@mui/material/Chip';
import { Tooltip } from '@mui/material';

import { fDateRangeShortLabel } from 'src/utils/format-time';

import { useFiltersContext } from 'src/providers/FiltersProvider';
import { useStoreLocations } from 'src/providers/StoreLocationsProvider';
import { REPORT_SOURCES, REPORT_CUSTOMER_TYPES } from 'src/pages/reports/common/consts';

import { chipProps, FiltersBlock, FiltersResult } from 'src/components/filters-result';

type FiltersResultsProps = {
  slots: {
    date?: boolean;
    source?: boolean;
    location?: boolean;
    customerType?: boolean;
    repeatType?: boolean;
    threshold?: boolean;
  };
};

export function FiltersResults({
  slots = {
    date: true,
    source: true,
    location: true,
    customerType: true,
    repeatType: false,
    threshold: false,
  },
}: {
  slots?: FiltersResultsProps['slots'];
}) {
  const { data: storeLocations } = useStoreLocations();
  const { filters } = useFiltersContext();

  const getLocationsResult = useCallback(() => {
    const { locationIds } = filters;

    if (!locationIds.length) {
      return 'All Stores';
    }

    return storeLocations
      ?.filter((location) => locationIds.includes(location.documentId))
      .map((location) => location.name)
      .join(', ');
  }, [storeLocations, filters]);

  const currentSource = useMemo(
    () =>
      REPORT_SOURCES.find((source) => source.value === filters.source)?.label ||
      'Please select a source',
    [filters.source]
  );

  const getCustomerTypesResult = useCallback(() => {
    const { customerType } = filters;

    if (customerType) {
      return REPORT_CUSTOMER_TYPES.find((type) => type.value === customerType)?.label || 'All';
    }

    return 'All';
  }, [filters]);

  return (
    <FiltersResult>
      <FiltersBlock label="Source:" isShow={Boolean(filters.source && slots.source)}>
        <Chip {...chipProps} label={currentSource} />
      </FiltersBlock>
      <FiltersBlock
        label="Date range:"
        isShow={Boolean(filters.dateFrom && filters.dateTo && slots.date)}
      >
        <Chip {...chipProps} label={fDateRangeShortLabel(filters.dateFrom, filters.dateTo)} />
      </FiltersBlock>
      <FiltersBlock label="Stores:" isShow={Boolean(slots.location)}>
        <Tooltip title={getLocationsResult()}>
          <Chip {...chipProps} sx={{ maxWidth: 250 }} label={getLocationsResult()} />
        </Tooltip>
      </FiltersBlock>
      <FiltersBlock
        label="Customer type:"
        isShow={Boolean(filters.customerType && slots.customerType)}
      >
        <Tooltip title={getCustomerTypesResult()}>
          <Chip {...chipProps} sx={{ maxWidth: 250 }} label={getCustomerTypesResult()} />
        </Tooltip>
      </FiltersBlock>
      <FiltersBlock label="Repeat type:" isShow={Boolean(filters.repeatType && slots.repeatType)}>
        <Chip {...chipProps} label={filters.repeatType} />
      </FiltersBlock>
      <FiltersBlock label="Order threshold:" isShow={Boolean(filters.threshold && slots.threshold)}>
        <Chip {...chipProps} label={filters.threshold} />
      </FiltersBlock>
    </FiltersResult>
  );
}
